/**
 * School money — the rules every finance screen shares, with no database calls (so they can be tested):
 * an invoice's balance and status, due dates, terms, receipt and registration numbers, and how an
 * invoice is built from the fee templates (with scholarships and discounts).
 */
import type { FeeCategory, FeeTemplate, Invoice, InvoiceItem, Student } from '../../types';

const DAY = 86_400_000;

export const naira = (n: number | undefined) => `₦${Math.round(Number(n) || 0).toLocaleString('en-NG')}`;

/** "First Term" / "1" / 1 → 1 (older invoices store the term as words) */
export function termNumber(term: unknown): number {
    if (typeof term === 'number') return term;
    const t = String(term || '').toLowerCase();
    if (/\b(1|first|1st)\b/.test(t)) return 1;
    if (/\b(2|second|2nd)\b/.test(t)) return 2;
    if (/\b(3|third|3rd)\b/.test(t)) return 3;
    return Number(t) || 0;
}
export const TERM_LABEL: Record<number, string> = { 1: 'First Term', 2: 'Second Term', 3: 'Third Term' };
export const termLabel = (term: unknown) => TERM_LABEL[termNumber(term)] || String(term || '');

/** a date stored as a number, an ISO string or a Firestore timestamp → milliseconds */
export function toMs(v: unknown): number | undefined {
    if (v == null || v === '') return undefined;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? t : undefined; }
    const o = v as { toMillis?: () => number; seconds?: number };
    if (typeof o.toMillis === 'function') return o.toMillis();
    if (typeof o.seconds === 'number') return o.seconds * 1000;
    return undefined;
}

/** the day an invoice is due. Older invoices were saved with their creation time as the due date: give those 30 days. */
export function dueMs(inv: Pick<Invoice, 'dueDate' | 'createdAt'>): number | undefined {
    const due = toMs(inv.dueDate), created = toMs(inv.createdAt);
    if (due !== undefined && created !== undefined && Math.abs(due - created) < DAY) return created + 30 * DAY;
    return due ?? (created !== undefined ? created + 30 * DAY : undefined);
}

export const balanceOf = (inv: Pick<Invoice, 'total' | 'amountPaid'>) => Math.max(0, Math.round(((Number(inv.total) || 0) - (Number(inv.amountPaid) || 0)) * 100) / 100);

export type InvoiceState = 'Paid' | 'Partially Paid' | 'Pending' | 'Overdue' | 'Scholarship' | 'Void';
/** what an invoice's status is now (worked out, so "Overdue" happens by itself when the due date passes) */
export function invoiceState(inv: Invoice & { void?: boolean }, now = Date.now()): InvoiceState {
    if (inv.void) return 'Void';
    const total = Number(inv.total) || 0;
    if (total === 0) return inv.items?.length ? 'Scholarship' : 'Paid';
    if (balanceOf(inv) <= 0) return 'Paid';
    const due = dueMs(inv);
    if (due !== undefined && due < now) return 'Overdue';
    return (Number(inv.amountPaid) || 0) > 0 ? 'Partially Paid' : 'Pending';
}
/** the status saved on the invoice after a payment or a reversal */
export const storedStatus = (total: number, paid: number): Invoice['status'] => (total === 0 ? 'Scholarship' : paid >= total - 0.005 ? 'Paid' : paid > 0 ? 'Partially Paid' : 'Pending');

/** "Kaduna Cambridge Academy" → "KCA" */
export function schoolInitials(name: string | undefined): string {
    const w = String(name || 'School').replace(/\(.*?\)/g, ' ').split(/[^A-Za-z]+/).filter(x => x && !/^(of|the|and|for|ltd|limited)$/i.test(x));
    return (w.map(x => x[0]).join('').toUpperCase() || 'SCH').slice(0, 5);
}
/** KCA/2026/000123 */
export const receiptNumber = (prefix: string, year: number, n: number) => `${prefix}/${year}/${String(n).padStart(6, '0')}`;
/** KCA/2026/0001 */
export const registrationNumber = (prefix: string, year: number, n: number) => `${prefix}/${year}/${String(n).padStart(4, '0')}`;

export const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'POS', 'Cheque', 'Online'] as const;
export type PaymentMethod = typeof PAYMENT_METHODS[number] | 'Stripe' | 'Paystack' | 'Flutterwave' | 'Admin Verification';

/** the invoice lines for one student from the chosen fee templates, with their scholarship applied */
export function invoiceItemsFor(student: Pick<Student, 'scholarship'>, templates: FeeTemplate[], categories: FeeCategory[]): InvoiceItem[] {
    const items: InvoiceItem[] = templates.flatMap(t => t.items.map(it => ({
        categoryId: it.categoryId,
        description: categories.find(c => c.id === it.categoryId)?.name || 'Fee',
        amount: Number(it.amount) || 0,
    })));
    const sch = student.scholarship;
    if (!sch?.active) return items;
    const full = ['Orphanage', 'SingleMother', 'Family'].includes(sch.type);
    const half = ['Merit', 'TeachersChild'].includes(sch.type);
    // the scholarship covers school (tuition) fees only, as before
    return items.map(it => {
        if (!/school|tuition/i.test(it.description)) return it;
        if (full) return { ...it, amount: 0, description: `${it.description} (Scholarship: ${sch.type})` };
        if (half) return { ...it, amount: it.amount / 2, description: `${it.description} (Scholarship: ${sch.type} - 50%)` };
        return it;
    });
}

/** a discount on an invoice: always worked out from the invoice's own lines, so applying one twice never compounds */
export function discountedTotal(items: InvoiceItem[], discount?: { type: 'percentage' | 'fixed'; value: number }): number {
    const gross = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
    if (!discount || !(discount.value > 0)) return gross;
    const off = discount.type === 'percentage' ? gross * Math.min(100, discount.value) / 100 : discount.value;
    return Math.max(0, Math.round((gross - off) * 100) / 100);
}

/** a student already has an invoice for this term and session (so it is not billed twice) */
export const alreadyBilled = (invoices: (Invoice & { void?: boolean })[], studentId: string, term: unknown, session: string, templateIds: string[]) =>
    invoices.some(i => !i.void && i.studentId === studentId && termNumber(i.term) === termNumber(term) && i.session === session
        && (!i.templateIds?.length || i.templateIds.some(t => templateIds.includes(t))));

/** a student's account: every invoice and payment in date order, with the running balance */
export interface StatementLine { at: number; kind: 'invoice' | 'payment' | 'reversal'; text: string; debit: number; credit: number; balance: number; ref?: string }
export function statement(invoices: (Invoice & { void?: boolean })[], payments: { amount: number; paidAt: unknown; receiptNo?: string; paymentRef?: string; method?: string; status?: string; reversedAt?: unknown; invoiceId: string }[]): StatementLine[] {
    const ev: Omit<StatementLine, 'balance'>[] = [];
    for (const i of invoices) if (!i.void) ev.push({ at: toMs(i.createdAt) || 0, kind: 'invoice', text: `${termLabel(i.term)} ${i.session} fees`, debit: Number(i.total) || 0, credit: 0, ref: i.id.slice(0, 8) });
    for (const p of payments) {
        ev.push({ at: toMs(p.paidAt) || 0, kind: 'payment', text: `Payment (${p.method || 'Payment'})`, debit: 0, credit: Number(p.amount) || 0, ref: p.receiptNo || p.paymentRef });
        if (p.status === 'reversed') ev.push({ at: toMs(p.reversedAt) || toMs(p.paidAt) || 0, kind: 'reversal', text: 'Payment reversed', debit: Number(p.amount) || 0, credit: 0, ref: p.receiptNo || p.paymentRef });
    }
    ev.sort((a, b) => a.at - b.at || (a.kind === 'invoice' ? -1 : 1));
    let bal = 0;
    return ev.map(e => { bal = Math.round((bal + e.debit - e.credit) * 100) / 100; return { ...e, balance: bal }; });
}

/** how long a debt has been owed, in the usual bands */
export function ageBand(daysOverdue: number): '0–30 days' | '31–60 days' | '61–90 days' | 'Over 90 days' {
    return daysOverdue <= 30 ? '0–30 days' : daysOverdue <= 60 ? '31–60 days' : daysOverdue <= 90 ? '61–90 days' : 'Over 90 days';
}
